import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../auth/authUtils';
import { useConfig } from '../../../contexts/configContext';
import { useOrganisationsContext } from '../../../contexts/useOrganisationsContext';
import { saveDeckPlanAsNetexToBackend } from '../api/deckPlanDetailsService';
import type { DeckPlan } from '../types/deckPlanTypes';
import { createOrUpdateDeckPlanRequest } from '../../../graphql/vehicles/mutations/createOrUpdateDeckPlan';
import { serializeDeckPlan } from '../api/fetchDeckPlans';

interface SaveResult {
  error: string | null;
}

interface UseDeckPlanSaveResult {
  save: (xml: string) => Promise<SaveResult>;
  saveGQL: (form: DeckPlan) => Promise<{ newId: string | null; error: string | null }>;
  saving: boolean;
  error: string | null;
  clearError: () => void;
}

/**
 * Save a deck plan via one of two paths sharing `saving`/`error` state:
 * - `save(xml)` — POSTs the full NeTEx XML to the import endpoint (edit flow
 *   for existing rows; keeps the route view's wire format).
 * - `saveGQL(form)` — fires the `createOrUpdateDeckPlan` GraphQL mutation with
 *   {@link serializeDeckPlan}'s full-replace input (create flow); resolves to
 *   `{ newId, error }` so the caller can advance the sidebar URL.
 *
 * Both resolve to a result object; never throw. Failures flow through `error`
 * and the per-call return so the editor surfaces them via SaveErrorSnackbar
 * without unmounting the slider.
 */
export function useDeckPlanSave(): UseDeckPlanSaveResult {
  const { t } = useTranslation();
  const { getAccessToken } = useAuth();
  const { applicationImportBaseUrl, applicationBaseUrl } = useConfig();
  const { currentOrganisation } = useOrganisationsContext();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = useCallback(
    async (xml: string): Promise<SaveResult> => {
      if (!applicationImportBaseUrl) {
        const message = t('error.noImportBaseUrl');
        setError(message);
        return { error: message };
      }
      if (!currentOrganisation?.id) {
        const message = t('deckPlans.save.noOrganisation');
        setError(message);
        return { error: message };
      }
      setSaving(true);
      setError(null);
      try {
        const token = await getAccessToken();
        await saveDeckPlanAsNetexToBackend(
          applicationImportBaseUrl,
          currentOrganisation.id,
          xml,
          token
        );
        return { error: null };
      } catch (e) {
        const message = e instanceof Error ? e.message : String(e);
        setError(message);
        return { error: message };
      } finally {
        setSaving(false);
      }
    },
    [applicationImportBaseUrl, getAccessToken, currentOrganisation?.id, t]
  );

  const saveGQL = useCallback(
    async (form: DeckPlan): Promise<{ newId: string | null; error: string | null }> => {
      if (!applicationBaseUrl) {
        const message = t('error.noBaseUrl');
        setError(message);
        return { newId: null, error: message };
      }
      if (!currentOrganisation?.id) {
        const message = t('deckPlans.save.noOrganisation');
        setError(message);
        return { newId: null, error: message };
      }
      setSaving(true);
      setError(null);
      try {
        const token = await getAccessToken();
        const body = await createOrUpdateDeckPlanRequest(
          applicationBaseUrl,
          token,
          serializeDeckPlan(form, currentOrganisation.id)
        );
        return { newId: body.createOrUpdateDeckPlan, error: null };
      } catch (err) {
        const message = err instanceof Error ? err.message : String(err);
        setError(message);
        return { newId: null, error: message };
      } finally {
        setSaving(false);
      }
    },
    [applicationBaseUrl, getAccessToken, currentOrganisation?.id, t]
  );

  return { save, saveGQL, saving, error, clearError: () => setError(null) };
}
